import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { load as parseYaml } from 'js-yaml';
import { DialogModule } from 'primeng/dialog';
import { FileApiService } from '@angular-workspace/services';
import type { ApiExplorerContext } from './api-explorer.models';
import { API_EXPLORER_SERVER_URL, API_EXPLORER_THEME } from './api-explorer-tokens';
import { DEFAULT_API_EXPLORER_THEME } from './api-explorer-default-theme';

export type { ApiExplorerContext, ApiExplorerResourceServer, ApiExplorerTheme } from './api-explorer.models';

interface TryOutSpec {
  openapi?: string;
  info?: { title?: string; version?: string; description?: string };
  servers?: Array<{ url: string; description?: string }>;
  paths?: Record<string, Record<string, unknown>>;
}

interface TryOutParameter {
  name: string;
  in: 'path' | 'query' | 'header';
  required: boolean;
  description: string;
  type: string;
  example: string;
}

interface TryOutOperation {
  id: string;
  method: string;
  path: string;
  summary: string;
  description: string;
  parameters: TryOutParameter[];
  requestContentType: string | null;
  requestBodyExample: string;
}

interface TryOutRequestState {
  params: Record<string, string>;
  bodyText: string;
}

interface TryOutExecutionResult {
  loading: boolean;
  status?: number;
  statusText?: string;
  body?: string;
  error?: string;
  curl?: string;
}

@Component({
  selector: 'lib-api-explorer',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule],
  templateUrl: './api-explorer.component.html',
  styleUrl: './api-explorer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ApiExplorerComponent {
  private readonly http = inject(HttpClient);
  private readonly keycloak = inject(KeycloakService);
  private readonly fileApiService = inject(FileApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly themeConfig = inject(API_EXPLORER_THEME, { optional: true }) ?? DEFAULT_API_EXPLORER_THEME;
  private readonly apiExplorerServerUrl = inject(API_EXPLORER_SERVER_URL, { optional: true }) ?? '';

  private readonly fileTryOutUrl = '/documents/file.yaml';
  private readonly ngsiLdTryOutUrl = '/documents/ngsi-ld.yaml';
  private readonly ogcTryOutUrl = '/documents/ogc.yaml';
  private loadedTryOutYamlUrl: string | null = null;
  private tryOutDefaultFileKey: string | null = null;

  visible = input(false);
  context = input<ApiExplorerContext | null>(null);

  visibleChange = output<boolean>();
  loginRequired = output<void>();

  readonly tryOutYamlLoading = signal(false);
  readonly tryOutYamlError = signal('');
  readonly tryOutSpec = signal<TryOutSpec | null>(null);
  readonly tryOutOperations = signal<TryOutOperation[]>([]);
  readonly selectedTryOutServerUrl = signal('');
  readonly expandedTryOutOperationId = signal<string | null>(null);
  readonly tryOutRequestState = signal<Record<string, TryOutRequestState>>({});
  readonly tryOutExecutionResults = signal<Record<string, TryOutExecutionResult>>({});

  /** Writable dialog state — PrimeNG needs two-way binding; parent `visible` input stays read-only. */
  dialogVisible = false;

  get themeStyles(): Record<string, string> {
    const theme = this.themeConfig;
    return {
      '--ae-accent': theme.accent,
      '--ae-accent-10': theme.accent10,
      '--ae-accent-15': theme.accent15,
      '--ae-accent-20': theme.accent20,
      '--ae-primary-color': theme.primaryColor,
      '--ae-primary-button-end': theme.primaryButtonGradientEnd,
      '--ae-text-dark': theme.textDark,
      '--ae-text-secondary': theme.textSecondary,
      '--ae-border-color': theme.borderColor,
      '--ae-bg-light': theme.bgLight,
      '--ae-surface': theme.surface,
      '--ae-modal-background': theme.modalBackground,
      '--ae-operations-panel-border': theme.operationsPanelBorder,
      '--ae-operations-panel-background': theme.operationsPanelBackground,
      '--ae-response-card-border': theme.responseCardBorder,
      '--ae-response-card-background': theme.responseCardBackground,
      '--ae-card-border': theme.cardBorder,
      '--ae-card-background': theme.cardBackground,
      '--ae-card-active-border': theme.cardActiveBorder,
      '--ae-card-active-background': theme.cardActiveBackground,
      '--ae-card-active-shadow': theme.cardActiveShadow,
      '--ae-card-active-summary-background': theme.cardActiveSummaryBackground,
      '--ae-card-active-text': theme.cardActiveText,
      '--ae-summary-background': theme.summaryBackground,
      '--ae-dialog-header-border': theme.dialogHeaderBorder,
      '--ae-focus-ring': theme.focusRing,
    };
  }

  constructor() {
    effect(() => {
      const parentVisible = this.visible();
      if (this.dialogVisible !== parentVisible) {
        this.dialogVisible = parentVisible;
        this.cdr.markForCheck();
      }
    });

    effect(() => {
      if (this.visible() && this.context()) {
        void this.loadExplorer();
      }
    });
  }

  readonly activeTryOutOperation = () => {
    const operations = this.tryOutOperations();
    const expandedId = this.expandedTryOutOperationId();
    if (!expandedId) {
      return operations[0] ?? null;
    }
    return operations.find((operation) => operation.id === expandedId) ?? null;
  };

  activeTryOutExecutionResult(): TryOutExecutionResult | null {
    const operation = this.activeTryOutOperation();
    if (!operation) {
      return null;
    }
    return this.tryOutExecutionResults()[operation.id] ?? null;
  }

  onDialogVisibleChange(visible: boolean): void {
    this.dialogVisible = visible;
    this.onVisibleChange(visible);
  }

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
    if (!visible) {
      this.tryOutYamlLoading.set(false);
      this.tryOutYamlError.set('');
    }
  }

  private closeDialog(): void {
    if (!this.dialogVisible) {
      return;
    }
    this.dialogVisible = false;
    this.onVisibleChange(false);
  }

  isTryOutOperationExpanded(operationId: string): boolean {
    return this.expandedTryOutOperationId() === operationId;
  }

  toggleTryOutOperation(operationId: string): void {
    this.expandedTryOutOperationId.set(operationId);
  }

  setTryOutParamValue(operationId: string, paramName: string, value: string): void {
    this.tryOutRequestState.update((state) => {
      const current = state[operationId];
      if (!current) return state;
      return {
        ...state,
        [operationId]: {
          ...current,
          params: { ...current.params, [paramName]: value },
        },
      };
    });
  }

  setTryOutRequestBody(operationId: string, value: string): void {
    this.tryOutRequestState.update((state) => {
      const current = state[operationId];
      if (!current) return state;
      return {
        ...state,
        [operationId]: { ...current, bodyText: value },
      };
    });
  }

  getTryOutMethodClass(method: string): string {
    return `try-out-custom__method try-out-custom__method--${method.toLowerCase()}`;
  }

  async resetTryOutOperation(operation: TryOutOperation): Promise<void> {
    const defaults = this.tryOutOperations().find((item) => item.id === operation.id);
    const state = this.tryOutRequestState()[operation.id];
    if (!defaults || !state) return;

    const authorizationValue = defaults.parameters.find(
      (parameter) => parameter.in === 'header' && parameter.name.toLowerCase() === 'authorization'
    )?.name;
    const currentAuthorizationToken = authorizationValue ? state.params[authorizationValue] : undefined;
    const token = currentAuthorizationToken?.startsWith('Bearer ')
      ? currentAuthorizationToken.slice(7)
      : undefined;

    const params = { ...state.params };
    defaults.parameters.forEach((parameter) => {
      params[parameter.name] = this.getDefaultTryOutParamValue(parameter, token);
    });

    this.tryOutRequestState.update((current) => ({
      ...current,
      [operation.id]: {
        params,
        bodyText: this.getDefaultTryOutRequestBody(defaults),
      },
    }));
    this.tryOutExecutionResults.update((current) => ({
      ...current,
      [operation.id]: { loading: false },
    }));
  }

  async executeCustomTryOut(operation: TryOutOperation): Promise<void> {
    this.expandedTryOutOperationId.set(operation.id);
    const state = this.tryOutRequestState()[operation.id];
    if (!state) return;

    const isLoggedIn = await this.keycloak.isLoggedIn().catch(() => false);
    if (!isLoggedIn) {
      this.closeDialog();
      this.loginRequired.emit();
      this.tryOutExecutionResults.update((current) => ({
        ...current,
        [operation.id]: {
          loading: false,
          error: 'Please login to execute this API.',
        },
      }));
      this.cdr.markForCheck();
      return;
    }

    const result: TryOutExecutionResult = { loading: true };
    this.tryOutExecutionResults.update((current) => ({
      ...current,
      [operation.id]: result,
    }));

    try {
      const token = await this.keycloak.getToken().catch(() => undefined);
      const headers: Record<string, string> = {};
      let resolvedPath = operation.path;
      const queryParams = new URLSearchParams();

      operation.parameters.forEach((parameter) => {
        const value = state.params[parameter.name] ?? '';
        if (!value) return;

        if (parameter.in === 'path') {
          resolvedPath = resolvedPath.replace(`{${parameter.name}}`, encodeURIComponent(value));
        } else if (parameter.in === 'query') {
          queryParams.set(parameter.name, value);
        } else if (parameter.in === 'header') {
          headers[parameter.name] = value;
        }
      });

      if (!headers['Authorization'] && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let body: BodyInit | undefined;
      if (operation.requestContentType) {
        if (operation.requestContentType === 'application/json' && state.bodyText.trim()) {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(JSON.parse(state.bodyText));
        } else if (
          operation.requestContentType === 'application/x-www-form-urlencoded' &&
          state.bodyText.trim()
        ) {
          headers['Content-Type'] = 'application/x-www-form-urlencoded';
          const parsedBody = JSON.parse(state.bodyText) as Record<string, unknown>;
          const formBody = new URLSearchParams();
          Object.entries(parsedBody ?? {}).forEach(([key, value]) => {
            formBody.set(key, value == null ? '' : String(value));
          });
          body = formBody.toString();
        }
      }

      const spec = this.tryOutSpec();
      const baseUrl = this.selectedTryOutServerUrl() || spec?.servers?.[0]?.url || '';
      const queryString = queryParams.toString();
      const requestUrl = `${baseUrl}${resolvedPath}${queryString ? `?${queryString}` : ''}`;

      result.curl = this.buildTryOutCurl(operation.method, requestUrl, headers, body);

      const response = await fetch(requestUrl, {
        method: operation.method,
        headers,
        body,
      });

      const responseText = await response.text();
      let formattedBody = responseText;
      try {
        formattedBody = JSON.stringify(JSON.parse(responseText), null, 2);
      } catch {
        // keep raw text
      }

      result.loading = false;
      result.status = response.status;
      result.statusText = response.statusText;
      result.body = formattedBody;
      result.error = response.ok ? undefined : `Request failed with status ${response.status}`;
    } catch (error: unknown) {
      result.loading = false;
      result.error = (error as Error)?.message || 'Request execution failed.';
    } finally {
      this.tryOutExecutionResults.update((current) => ({
        ...current,
        [operation.id]: { ...result },
      }));
      this.cdr.markForCheck();
    }
  }

  private async loadExplorer(): Promise<void> {
    this.tryOutYamlError.set('');
    this.tryOutYamlLoading.set(true);
    this.cdr.markForCheck();

    try {
      await this.ensureTryOutSpecLoaded();
    } catch (error) {
      console.error('Error loading API Explorer documentation:', error);
      this.tryOutYamlError.set('Unable to load the API documentation right now.');
    } finally {
      this.tryOutYamlLoading.set(false);
      this.cdr.markForCheck();
    }
  }

  private async ensureTryOutSpecLoaded(): Promise<void> {
    const targetYamlUrl = this.getTryOutYamlUrlForDataset();

    if (
      this.tryOutSpec() &&
      this.tryOutOperations().length > 0 &&
      this.loadedTryOutYamlUrl === targetYamlUrl
    ) {
      return;
    }

    const yamlText = await firstValueFrom(
      this.http.get(targetYamlUrl, { responseType: 'text' })
    );

    const parsed = parseYaml(yamlText) as TryOutSpec;
    const envServerUrl = this.apiExplorerServerUrl.trim();
    if (envServerUrl) {
      const existingServers = parsed?.servers ?? [];
      const normalizedEnvServerUrl = envServerUrl.replace(/\/+$/, '');
      const envServerAlreadyPresent = existingServers.some(
        (server) => (server?.url || '').trim().replace(/\/+$/, '') === normalizedEnvServerUrl
      );
      if (!envServerAlreadyPresent) {
        parsed.servers = [{ url: envServerUrl }, ...existingServers];
      }
    }

    this.tryOutSpec.set(parsed);
    this.loadedTryOutYamlUrl = targetYamlUrl;
    this.selectedTryOutServerUrl.set(envServerUrl || parsed?.servers?.[0]?.url || '');
    const operations = this.buildTryOutOperations(parsed);
    this.tryOutOperations.set(operations);
    this.expandedTryOutOperationId.set(operations[0]?.id ?? null);
    await this.initializeTryOutState(operations);
  }

  private getTryOutYamlUrlForDataset(): string {
    const ctx = this.context();
    const candidates: string[] = [];

    const rs = ctx?.resourceServers ?? [];
    rs.forEach((server) => {
      candidates.push(
        String(server.type ?? ''),
        String(server.name ?? ''),
        String(server.resourceType ?? ''),
        String(server.datasetType ?? '')
      );
    });

    const apis = ctx?.iudxResourceAPIs;
    if (Array.isArray(apis)) {
      apis.forEach((api) => candidates.push(String(api ?? '')));
    }

    candidates.push(String(ctx?.resourceType ?? ''));

    const normalized = candidates.map((v) => v.trim().toLowerCase()).filter(Boolean);

    const hasOgc = normalized.some((v) => v.includes('ogc'));
    if (hasOgc) return this.ogcTryOutUrl;

    const hasNgsiLd = normalized.some((v) => v === 'ngsi-ld' || v === 'ngsi_ld' || v === 'ngsild');
    if (hasNgsiLd) return this.ngsiLdTryOutUrl;

    const hasFile = normalized.some((v) => v === 'file');
    if (hasFile) return this.fileTryOutUrl;

    return this.fileTryOutUrl;
  }

  private buildTryOutOperations(spec: TryOutSpec): TryOutOperation[] {
    const operations: TryOutOperation[] = [];
    const validMethods = new Set(['get', 'post', 'put', 'delete', 'patch', 'head', 'options']);
    const paths = spec.paths ?? {};

    Object.entries(paths).forEach(([path, pathConfig]) => {
      const pathObj = pathConfig as Record<string, unknown>;
      const sharedParameters = Array.isArray(pathObj['parameters'])
        ? (pathObj['parameters'] as unknown[])
        : [];

      Object.entries(pathObj ?? {}).forEach(([method, operation]) => {
        if (!validMethods.has(method.toLowerCase())) {
          return;
        }

        const op = operation as Record<string, unknown>;
        const operationParameters = Array.isArray(op['parameters']) ? (op['parameters'] as unknown[]) : [];
        const mergedParameters = this.mergeTryOutParameters(sharedParameters, operationParameters);
        const requestContent = (op['requestBody'] as { content?: Record<string, unknown> } | undefined)?.content ?? {};
        const requestContentEntries = Object.entries(requestContent);
        const [requestContentType, requestContentValue] = requestContentEntries[0] ?? [null, null];

        operations.push({
          id: `${method}:${path}`,
          method: method.toUpperCase(),
          path,
          summary: String(op['summary'] ?? ''),
          description: String(op['description'] ?? ''),
          parameters: mergedParameters.map((param) => {
            const p = param as Record<string, unknown>;
            const schema = p['schema'] as Record<string, unknown> | undefined;
            return {
              name: String(p['name'] ?? ''),
              in: (p['in'] ?? 'query') as 'path' | 'query' | 'header',
              required: !!p['required'],
              description: String(p['description'] ?? ''),
              type: String(schema?.['type'] ?? 'string'),
              example: String(p['example'] ?? schema?.['example'] ?? ''),
            };
          }),
          requestContentType: requestContentType as string | null,
          requestBodyExample: this.formatTryOutExample(
            (requestContentValue as { example?: unknown } | null)?.example
          ),
        });
      });
    });

    return operations.filter((operation) => !this.isTokenEndpointOperation(operation));
  }

  private isTokenEndpointOperation(operation: TryOutOperation): boolean {
    const normalizedPath = operation.path.toLowerCase();
    const normalizedSummary = (operation.summary || '').toLowerCase();
    const normalizedDescription = (operation.description || '').toLowerCase();

    const pathLooksLikeToken =
      normalizedPath === '/token' ||
      normalizedPath.endsWith('/token') ||
      normalizedPath.includes('/token/');
    const textLooksLikeToken =
      normalizedSummary.includes('token') || normalizedDescription.includes('token');

    return pathLooksLikeToken || textLooksLikeToken;
  }

  private mergeTryOutParameters(sharedParameters: unknown[], operationParameters: unknown[]): unknown[] {
    const merged = new Map<string, unknown>();
    [...sharedParameters, ...operationParameters].forEach((parameter) => {
      const p = parameter as Record<string, unknown>;
      const key = `${String(p['name'] ?? '')}:${String(p['in'] ?? '')}`;
      merged.set(key, parameter);
    });
    return Array.from(merged.values());
  }

  private formatTryOutExample(example: unknown): string {
    if (example == null || example === '') {
      return '';
    }
    if (typeof example === 'string') {
      return example;
    }
    try {
      return JSON.stringify(example, null, 2);
    } catch {
      return String(example);
    }
  }

  private getDefaultTryOutParamValue(parameter: TryOutParameter, token?: string): string {
    const normalizedName = parameter.name.toLowerCase();
    const datasetId = this.context()?.datasetId;

    if (parameter.in === 'header' && normalizedName === 'authorization' && token) {
      return `Bearer ${token}`;
    }

    if (
      parameter.in === 'path' &&
      (normalizedName === 'databankid' || normalizedName === 'datasetid') &&
      datasetId
    ) {
      return datasetId;
    }

    return parameter.example ?? '';
  }

  private async initializeTryOutState(operations: TryOutOperation[]): Promise<void> {
    const token = await this.keycloak.getToken().catch(() => undefined);
    this.tryOutDefaultFileKey = await this.resolveTryOutDefaultFileKey();
    const requestState: Record<string, TryOutRequestState> = {};
    const executionResults: Record<string, TryOutExecutionResult> = {};

    operations.forEach((operation) => {
      const params = operation.parameters.reduce<Record<string, string>>((acc, parameter) => {
        acc[parameter.name] = this.getDefaultTryOutParamValue(parameter, token);
        return acc;
      }, {});

      requestState[operation.id] = {
        params,
        bodyText: this.getDefaultTryOutRequestBody(operation),
      };
      executionResults[operation.id] = { loading: false };
    });

    this.tryOutRequestState.set(requestState);
    this.tryOutExecutionResults.set(executionResults);
  }

  private isFileDownloadTryOutOperation(operation: TryOutOperation): boolean {
    return operation.path.toLowerCase().includes('/files/download');
  }

  private getDefaultTryOutRequestBody(operation: TryOutOperation): string {
    if (!this.isFileDownloadTryOutOperation(operation)) {
      return operation.requestBodyExample;
    }
    return this.buildTryOutRequestBodyWithFileKey(
      operation.requestBodyExample,
      this.tryOutDefaultFileKey
    );
  }

  private buildTryOutRequestBodyWithFileKey(exampleBody: string, fileKey?: string | null): string {
    const key = (fileKey ?? '').trim();
    if (!key || !exampleBody.trim()) {
      return exampleBody;
    }
    try {
      const parsed = JSON.parse(exampleBody) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object' && 'key' in parsed) {
        parsed['key'] = key;
        return JSON.stringify(parsed, null, 2);
      }
    } catch {
      // keep original example when body is not JSON
    }
    return exampleBody;
  }

  private async resolveTryOutDefaultFileKey(): Promise<string | null> {
    const ctx = this.context();
    const fromContext = (ctx?.fileKeys ?? [])
      .map((k) => String(k).trim())
      .filter(Boolean);
    if (fromContext.length) {
      return fromContext[0];
    }

    const datasetId = ctx?.datasetId;
    if (!datasetId) {
      return null;
    }

    try {
      const res = await this.fileApiService.getFilesForPreview(datasetId);
      const files = this.extractFilesFromFilesResponse(res?.data);
      const keys = files
        .filter((f) => f.isFile)
        .map((f) => f.key)
        .filter((k) => k.trim().length > 0);
      keys.sort((a, b) => a.localeCompare(b));
      return keys[0] ?? null;
    } catch {
      return null;
    }
  }

  private extractFilesFromFilesResponse(
    data: unknown
  ): Array<{ key: string; isFile: boolean }> {
    if (!data || typeof data !== 'object') return [];
    const root = data as Record<string, unknown>;
    const rawFiles = Array.isArray(root['files'])
      ? root['files']
      : Array.isArray((root['data'] as Record<string, unknown> | undefined)?.['files'])
        ? ((root['data'] as Record<string, unknown>)['files'] as unknown[])
        : [];
    return rawFiles
      .map((f) => {
        if (!f || typeof f !== 'object') return null;
        const row = f as Record<string, unknown>;
        const key = String(row['key'] ?? row['name'] ?? '').trim();
        if (!key) return null;
        return { key, isFile: row['isFile'] !== false };
      })
      .filter((f): f is { key: string; isFile: boolean } => f != null);
  }

  private buildTryOutCurl(
    method: string,
    url: string,
    headers: Record<string, string>,
    body?: BodyInit
  ): string {
    const headerFlags = Object.entries(headers)
      .map(([key, value]) => `-H "${key}: ${value}"`)
      .join(' ');

    let bodyFlag = '';
    if (typeof body === 'string' && body.length > 0) {
      bodyFlag = ` --data '${body.replace(/'/g, "\\'")}'`;
    }

    return `curl -X ${method} "${url}"${headerFlags ? ` ${headerFlags}` : ''}${bodyFlag}`;
  }
}
